/**
 * Wire-shape mirrors for `backend/sherlock/schemas/feedback.py`.
 *
 * snake_case on the wire (matches the Pydantic schemas verbatim). Datetimes
 * are ISO-8601 strings; UUIDs are plain strings. Only convert when rendering.
 *
 * Source of truth: when the Pydantic schemas change, update this file to
 * match. QA's Layer 2 verification checks the two are byte-for-byte equivalent
 * on the wire.
 */

/** Which kind of source the feedback row points at. */
export type FeedbackSourceType = 'investigation' | 'analysis';

/** Thumb direction: -1 for down, +1 for up. Zero is invalid. */
export type FeedbackThumb = -1 | 1;

/**
 * Controlled vocabulary of tags. Keep this in sync with
 * ``ALLOWED_TAGS`` in ``backend/sherlock/schemas/feedback.py``.
 *
 * Order is intentional — positive-leaning first, then accuracy concerns, then
 * process/data coverage. The feedback widget renders chips in this order.
 */
export const ALLOWED_FEEDBACK_TAGS = [
  'accurate',
  'wrong_root_cause',
  'missed_trigger',
  'missing_data',
  'too_long',
  'helpful_for_handoff',
  'would_share',
] as const;

export type FeedbackTag = (typeof ALLOWED_FEEDBACK_TAGS)[number];

/** Human-friendly labels rendered on the chips. */
export const FEEDBACK_TAG_LABELS: Record<FeedbackTag, string> = {
  accurate: 'Accurate',
  wrong_root_cause: 'Wrong root cause',
  missed_trigger: 'Missed trigger',
  missing_data: 'Missing data',
  too_long: 'Too long',
  helpful_for_handoff: 'Helpful for handoff',
  would_share: 'Would share',
};

/** POST /api/feedback/ — request body. */
export interface FeedbackCreate {
  source_type: FeedbackSourceType;
  source_id: string;
  thumb: FeedbackThumb;
  tags: FeedbackTag[];
  note?: string | null;
}

/** Returned by POST /api/feedback/ and the two GET endpoints. */
export interface FeedbackOut {
  id: string;
  source_type: FeedbackSourceType;
  source_id: string;
  thumb: FeedbackThumb;
  tags: FeedbackTag[];
  note: string | null;
  created_at: string;
  updated_at: string;
}

/** Returned by GET /api/feedback/{source_type}/{source_id}/summary/. */
export interface FeedbackSummary {
  thumb_up: number;
  thumb_down: number;
  tag_counts: Partial<Record<FeedbackTag, number>>;
  my_feedback: FeedbackOut | null;
}
